import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import pMinDelay from 'p-min-delay';
import loadable from '@loadable/component';
import { ListItem, ListItemAvatar, ListItemText, Skeleton } from '@mui/material';

import { type ModuleSearchOptionsListItemProps } from 'app/main/components/fields/components/useModuleSearch';

type ModuleOptionsListItemProps = ModuleSearchOptionsListItemProps & {
	optionProps?: React.HTMLAttributes<HTMLLIElement>;
	fallbackLabel?: string;
};

const fallback = (
	<>
		<ListItemAvatar>
			<Skeleton className="h-44 w-44" variant="circular" />
		</ListItemAvatar>
		<ListItemText primary={<Skeleton variant="text" />} secondary={<Skeleton variant="text" />} disableTypography />
	</>
);

const modulesPrefixes = ['CommercialCampaigns', 'MarketingActions', 'WorkReports'];

function getModuleOptionsListItemPrefix(currentModule: string) {
	const prefixes = modulesPrefixes.reduce((prev, prefix) => ({ ...prev, [prefix.toLowerCase()]: prefix }), {});
	return prefixes[currentModule] || `${currentModule.charAt(0).toUpperCase()}${currentModule.slice(1)}`;
}

const OptionsListItemLayout = loadable<ModuleSearchOptionsListItemProps>(
	({ settings }) => {
		const cleanedModule = settings.modu_title.replaceAll('_', '');
		const modulePrefix = getModuleOptionsListItemPrefix(cleanedModule);
		return pMinDelay(import(`../${cleanedModule}/${modulePrefix}OptionsListItem.tsx`), 200);
	},
	{
		cacheKey: ({ settings }) => `${settings.modu_title}OptionsListItem`,
		fallback
	}
);

export default function ModuleOptionsListItem({ fallbackLabel, optionProps, ...props }: ModuleOptionsListItemProps) {
	fallbackLabel = fallbackLabel || props.option[`${props.settings.modu_short}_title`];

	return (
		<ListItem {...optionProps} alignItems="flex-start" disablePadding>
			<ErrorBoundary fallback={<ListItemText primary={fallbackLabel} />}>
				<OptionsListItemLayout {...props} />
			</ErrorBoundary>
		</ListItem>
	);
}
